<template>
  <div class="template-events-reports-container">
    <TemplateEventsFilters
      ref="filters"
      :templateEventsData="filteredTemplateEvents"
      @filter-change="onFilterChange"
    />
    <div class="reports-wrap">
      <TemplateEventsSummary :events="filteredTemplateEvents" />
      <div class="bar-section full-width">
        <h5 class="chart-title">
          {{ $t('TEMPLATE_EVENTS_REPORT.CHART.BY_DATE') }}
        </h5>
        <TemplateEventsWhatsAppStatusChart
          :templateEvents="filteredTemplateEvents"
        />
      </div>
      <div class="bar-section">
        <div class="bar-section-group">
          <div class="chart-block">
            <h5 class="chart-title">
              {{ $t('TEMPLATE_EVENTS_REPORT.CHART.TOP_SEND_ERRORS') }}
            </h5>
            <TemplateEventsErrorsPieChart
              :templateEvents="filteredTemplateEvents"
            />
          </div>
          <div class="chart-block">
            <h5 class="chart-title">
              {{ $t('TEMPLATE_EVENTS_REPORT.CHART.MESSAGE_STATUSES') }}
            </h5>
            <TemplateEventsStatusPieChart
              :templateEvents="filteredTemplateEvents"
            />
          </div>
          <div class="chart-block">
            <h5 class="chart-title">
              {{ $t('TEMPLATE_EVENTS_REPORT.CHART.TEMPLATE_CATEGORIES') }}
            </h5>
            <TemplateEventsCategoryPieChart
              :templateEvents="filteredTemplateEvents"
            />
          </div>
        </div>
      </div>
      <div class="whatsapp-api-info">
        <p>
          <strong>{{ $t('TEMPLATE_EVENTS_REPORT.INFO.MESSAGE') }}</strong
          ><br />
          <a
            :href="$t('TEMPLATE_EVENTS_REPORT.INFO.LINK')"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ $t('TEMPLATE_EVENTS_REPORT.INFO.LINK') }}
          </a>
        </p>
      </div>
      <div class="table-section no-bg">
        <TemplateEventsReasonsTable
          :templateEventsData="filteredTemplateEvents"
        />
      </div>
    </div>
  </div>
</template>

<script>
import TemplateEventsReasonsTable from './components/TemplateEventsReasonsTable';
import TemplateEventsFilters from './components/TemplateEventsFilters';
import TemplateEventsReportApi from '../../../../api/inbox/historyTemplateEvents';
import TemplateEventsWhatsAppStatusChart from './components/TemplateEventsWhatsAppStatusChart';
import TemplateEventsErrorsPieChart from './components/TemplateEventsErrorsPieChart';
import TemplateEventsStatusPieChart from './components/TemplateEventsStatusPieChart';
import TemplateEventsCategoryPieChart from './components/TemplateEventsCategoryPieChart';
import TemplateEventsSummary from './components/TemplateEventsSummary';

export default {
  name: 'TemplateEventsReportsView',
  components: {
    TemplateEventsFilters,
    TemplateEventsReasonsTable,
    TemplateEventsWhatsAppStatusChart,
    TemplateEventsErrorsPieChart,
    TemplateEventsStatusPieChart,
    TemplateEventsCategoryPieChart,
    TemplateEventsSummary,
  },
  data() {
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return {
      templateEvents: [],
      filters: {
        customDateRange: [sevenDaysAgo, today],
        inboxes: [],
        users: [],
        messageStatuses: [],
        templates: [],
      },
    };
  },
  computed: {
    filteredTemplateEvents() {
      const {
        customDateRange = [],
        inboxes = [],
        users = [],
        messageStatuses = [],
        templates = [],
      } = this.filters;

      const [start, end] = customDateRange;

      return this.templateEvents.filter(item => {
        const createdDate = new Date(item.created_at);
        const createdDateOnly = new Date(
          createdDate.getFullYear(),
          createdDate.getMonth(),
          createdDate.getDate()
        );

        const startDate = start ? new Date(start) : null;
        const endDate = end ? new Date(end) : null;

        const dateMatch =
          !startDate ||
          !endDate ||
          (createdDateOnly >= startDate && createdDateOnly <= endDate);

        const inboxMatch = !inboxes.length || inboxes.includes(item.inbox_name);
        const userMatch = !users.length || users.includes(item.sender_name);
        const statusMatch =
          !messageStatuses.length || messageStatuses.includes(item.status);
        const templateMatch =
          !templates.length || templates.includes(item.template_name);

        return (
          dateMatch && inboxMatch && userMatch && statusMatch && templateMatch
        );
      });
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.$refs.filters.emitFilters();
    });
  },
  methods: {
    prepareTemplateEvents(rawMessages) {
      if (!Array.isArray(rawMessages)) return [];

      return rawMessages.map(msg => {
        const statusCode = parseInt(msg.status_code);
        const statusMap = {
          queued: 'queued',
          sent: 'sent',
          delivered: 'delivered',
          failed: 'failed',
          read: 'read',
        };

        const status = statusMap[msg.status_code] || 'unknown';

        return {
          ...msg,
            sender_name: msg.sender_name || this.$t('TEMPLATE_EVENTS_REPORT.DEFAULT_SENDER'),
          status,
          status_message: this.getStatusDescription(statusCode),
        };
      });
    },
    getStatusDescription(statusCode) {
      switch (parseInt(statusCode)) {
        case 0:
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.QUEUED');
        case 1:
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.SENT');
        case 2:
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.DELIVERED');
        case 3:
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.FAILED');
        default:
          return this.$t('TEMPLATE_EVENTS_REPORT.STATUS.UNKNOWN');
      }
    },
    async onFilterChange(newFilters) {
      this.filters = {
        ...this.filters,
        ...newFilters,
      };

      try {
        const [start, end] = this.filters.customDateRange || [];
        const formatDate = date => new Date(date).toISOString().split('T')[0];

        const response = await TemplateEventsReportApi.getAll({
          startDate: start ? formatDate(start) : null,
          endDate: end ? formatDate(end) : null,
        });

        const messages = Array.isArray(response.data)
          ? response.data
          : response.data?.data || [];

        this.templateEvents = this.prepareTemplateEvents(messages);
      } catch (error) {
        console.error(this.$t('TEMPLATE_EVENTS_REPORT.ERRORS.FETCH'), error);

        this.templateEvents = [];
      }
    },
  },
};
</script>

<style scoped>
.template-events-reports-container {
  display: flex;
  flex-direction: row;
  width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
  align-items: flex-start;
}

.template-events-reports-container > *:first-child {
  width: 280px;
  flex-shrink: 0;
  margin-right: 24px;
}

.reports-wrap {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  padding: 2vh 2vw 10vh;
  background: #f8fafc;
  overflow: hidden;
  min-width: 0;
  box-sizing: border-box;
}

.bar-section,
.table-section {
  margin-top: 4vh;
  background: white;
  padding: 20px;
  border-radius: 8px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  box-sizing: border-box;
  width: 100%;
  overflow-x: auto;
}

.table-section.no-bg {
  background: transparent;
  padding: 0;
  box-shadow: none;
}

.chart-block {
  flex: 1 1 30%;
  min-width: 280px;
  height: 320px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 0;
  background: transparent;
  border-radius: 0;
  box-shadow: none;
}

.chart-block canvas {
  width: 100% !important;
  height: 100% !important;
  max-width: 300px;
  max-height: 300px;
}

.bar-section-group {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 1rem;
  flex-wrap: nowrap;
}

.chart-title {
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 12px;
}

.bar-section.full-width {
  flex: 1 1 100%;
  margin-top: 2vh;
}

.bar-section.wide {
  flex: 1 1 70%;
  max-height: 500px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

.bar-section.narrow {
  flex: 1 1 28%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}

.whatsapp-api-info {
  margin-top: 20px;
  background-color: #333;
  color: white;
  padding: 20px;
  border-radius: 8px;
  font-size: 14px;
  line-height: 1.6;
}

.whatsapp-api-info a {
  color: #4fc3f7;
  text-decoration: underline;
  word-break: break-word;
}
</style>
